/**
 * Xhelos Debug Tools - Unified Version
 * Merges all debug functionality into one place
 */

import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import useUiEffectsStore from '@store/useUiEffectsStore';
import useCinematicStore from '@store/useCinematicStore';
import useMapStore from '@store/useMapStore';
import useSelectionStore from '@store/useSelectionStore';
import useMetadataStore from '@store/useMetadataStore';
import calculateActionOrigins from '@helpers/game/calculateActionOrigins';
import { validateGameSnapshots, validateUnitAcrossSnapshots, validateUnits } from './unitValidator';
import { CONFIG } from './constants';

// Create the unified debug object
const xhelosDebugger = {
  // === SNAPSHOT FUNCTIONS ===
  
  /**
   * Get RAW snapshot (not merged) - gets the actual delta or base/current
   */
  getRawSnapshot(key) {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame) {
      console.error('❌ No raw game data loaded');
      return null;
    }
    
    // In PLAN/DEPLOY phase, there might not be snapshots
    if (!rawGame.snapshots) {
      if (key === 'current' || key === 'base') {
        console.log(`📸 No snapshots in ${rawGame.status} phase - returning full game`);
        return rawGame;
      }
      console.warn('⚠️ No snapshots available in current game phase');
      return null;
    }
    
    const snapshot = rawGame.snapshots[key];
    if (!snapshot) {
      console.error(`❌ Raw snapshot '${key}' not found`);
      console.log('📌 Available snapshots:', Object.keys(rawGame.snapshots));
      return null;
    }
    
    // Check if this is a sparse delta (numbered snapshots)
    const isDelta = !isNaN(parseInt(key));
    const label = isDelta ? `Delta #${key}` : `Raw ${key}`;
    
    console.groupCollapsed(`📸 ${label} Snapshot`);
    
    // Show what's in this snapshot
    if (snapshot.dynamic_objects?.units) {
      const units = snapshot.dynamic_objects.units;
      if (isDelta && units.length < 10) {
        // For deltas, show full unit data since it's sparse
        console.log('Changed units:', units);
      } else {
        // For base/current, show table
        console.table(units.map(u => ({
          id: u.id,
          name: u.name || '❌ MISSING',
          hp: `${u.hp}/${u.max_hp || '?'}`,
          pos: `(${u.x}, ${u.y})`,
          team: u.teamClass || '?'
        })));
      }
    }
    
    console.log('Full snapshot:', snapshot);
    console.groupEnd();
    
    //return snapshot;
  },
  
  /**
   * Get MERGED/CALCULATED snapshot at a specific point
   */
  getSnapshot(key) {
    const state = useGameStore.getState();
    
    // Special case: get current view
    if (key === 'view' || key === undefined) {
      const game = state.game;
      console.log(`📸 Current view (merged snapshot at index: ${state.currentSnapshotIndex})`);
      return game;
    }
    
    // Use the store's getGameByIndex function which handles merging
    const mergedGame = state.getGameByIndex(key);
    
    if (!mergedGame) {
      console.error(`❌ Could not get merged snapshot for '${key}'`);
      return null;
    }
    
    console.groupCollapsed(`📸 Merged Snapshot: ${key}`);
    
    // Show merged units
    if (mergedGame.dynamic_objects?.units) {
      console.table(mergedGame.dynamic_objects.units.map(u => ({
        id: u.id,
        name: u.name || '❌ MISSING',
        hp: `${u.hp}/${u.max_hp || '?'}`,
        pos: `(${u.x}, ${u.y})`,
        team: u.teamClass || '?',
        alive: u.is_alive ? '✅' : '💀'
      })));
    }
    
    console.log('Full merged game:', mergedGame);
    console.groupEnd();
    
    //return mergedGame;
  },
  
  /**
   * Get only the delta changes at a specific step
   */
  getDeltaSnapshot(step) {
    if (typeof step !== 'number' || step < 0) {
      console.error('❌ Step must be a non-negative number');
      return null;
    }
    
    return this.getRawSnapshot(step);
  },
  
  /**
   * Show all available snapshots
   */
  listSnapshots() {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame?.snapshots) {
      console.error('❌ No snapshots available');
      return;
    }
    
    const snapshots = Object.keys(rawGame.snapshots);
    console.log('📸 Available snapshots:', snapshots);
    
    // Categorize them
    const base = snapshots.includes('base');
    const current = snapshots.includes('current');
    const deltas = snapshots.filter(k => !isNaN(parseInt(k))).sort((a, b) => parseInt(a) - parseInt(b));
    
    console.log('  Base:', base ? '✅' : '❌');
    console.log('  Current:', current ? '✅' : '❌');
    console.log('  Deltas:', deltas.length > 0 ? deltas.join(', ') : 'None');
    
    //return snapshots;
  },
  
  // === UNIT FUNCTIONS ===
  
  /**
   * Get a specific unit by ID from current game state
   */
  getUnit(unitId) {
    const unit = useGameStore.getState().getUnitById(unitId);

    if (unit) {
      console.log(`✅ Found Unit #${unitId}:`, unit);
      return unit;
    } else {
      console.error(`❌ Unit with ID #${unitId} not found.`);
      return null;
    }
  },
  
  /**
   * Get unit across all snapshots
   */
  getUnitAcrossSnapshots(unitId) {
    const state = useGameStore.getState();
    const rawGame = state._rawGame;
    
    if (!rawGame) {
      console.error('❌ No game loaded');
      return null;
    }
    
    const results = {};
    
    // Check in current game data
    if (rawGame.dynamic_objects?.units) {
      const unit = rawGame.dynamic_objects.units.find(u => u.id === unitId);
      if (unit) results.current_game = unit;
    }
    
    // Check in snapshots
    if (rawGame.snapshots) {
      // Base
      if (rawGame.snapshots.base?.dynamic_objects?.units) {
        const unit = rawGame.snapshots.base.dynamic_objects.units.find(u => u.id === unitId);
        if (unit) results.base = unit;
      }
      
      // Numbered
      Object.keys(rawGame.snapshots).forEach(key => {
        if (!isNaN(parseInt(key))) {
          const snapshot = rawGame.snapshots[key];
          if (snapshot?.dynamic_objects?.units) {
            const unit = snapshot.dynamic_objects.units.find(u => u.id === unitId);
            if (unit) results[`snapshot_${key}`] = unit;
          }
        }
      });
      
      // Current
      if (rawGame.snapshots.current?.dynamic_objects?.units) {
        const unit = rawGame.snapshots.current.dynamic_objects.units.find(u => u.id === unitId);
        if (unit) results.current = unit;
      }
    }
    
    if (Object.keys(results).length === 0) {
      console.error(`❌ Unit #${unitId} not found anywhere`);
      return null;
    }
    
    console.groupCollapsed(`🎮 Unit #${unitId} across snapshots`);
    Object.entries(results).forEach(([key, unit]) => {
      console.log(`📍 ${key}:`, unit);
    });
    console.groupEnd();
    
    //return results;
  },
  
  /**
   * List all units in current state
   */
  listUnits() {
    const units = useGameStore.getState().getCurrentUnits();
    if (!units || units.length === 0) {
      console.error('❌ No units found');
      return [];
    }
    
    console.table(units.map(u => ({
      id: u.id,
      name: u.name || '❌ MISSING',
      hp: `${u.hp || '?'}/${u.max_hp || '?'}`,
      ap: `${u.ap || '?'}/${u.max_ap || '?'}`,
      pos: `(${u.x || '?'}, ${u.y || '?'})`,
      team: u.teamClass || '❌',
      alive: u.is_alive ? '✅' : '💀'
    })));
    
    return units;
  },
  
  // === GAME STATE FUNCTIONS ===
  
  /**
   * Get current game state (computed)
   */
  getGameState() {
    console.log("Current Game State (Computed):", useGameStore.getState().game);
    //return useGameStore.getState().game;
  },
  
  /**
   * Get raw game data
   */
  getRawGameData() {
    const rawGame = useGameStore.getState()._rawGame;
   console.log('📦 Raw game data (_rawGame):', rawGame);
  },
  
  /**
   * Get all store states
   */
  getStores() {
    return {
      game: useGameStore.getState(),
      cinematic: useCinematicStore.getState(),
      map: useMapStore.getState(),
      selection: useSelectionStore.getState(),
      auth: useAuthStore.getState(),
      uiEffects: useUiEffectsStore.getState()
    };
  },
  
  // === RESOLUTION QUEUE ===
  
  /**
   * Get resolution queue item at index
   */
  getResolutionQueue(index) {
    const queue = useGameStore.getState().game?.resolution_queue;
    if (!queue) {
      console.error('❌ No resolution queue available');
      //return null;
    }
    
    if (index !== undefined) {
      console.log(`Resolution Queue [${index}]:`, queue[index]);
      //return queue[index];
    }
    
    console.log("Full Resolution Queue:", queue);
    //return queue;
  },
  
  /**
   * Dump resolution queue with formatting
   */
  dumpResolutionQueue() {
    const game = useGameStore.getState().game;
    if (!game?.resolution_queue) {
      console.error('❌ No resolution queue');
      return null;
    }
    
    console.groupCollapsed(`📋 Resolution Queue (${game.resolution_queue.length} items)`);
    game.resolution_queue.forEach((item, index) => {
      const icon = item.event_type === 'action' ? '⚔️' : 
                  item.event_type === 'effect_processing' ? '⚡' : '✨';
      console.log(`${index}: ${icon} ${item.event_type}`, {
        action: item.action_type,
        unit: item.unit_id,
        isReaction: item.is_reaction,
        animations: item.animations?.length || 0
      });
    });
    console.groupEnd();
    
    return game.resolution_queue;
  },
  
  /**
   * Get animations from resolution queue
   */
  getAnimation(index) {
    const game = useGameStore.getState().game;
    if (!game || !game.resolution_queue) {
      console.warn('❌ No resolution queue available');
      return null;
    }
    
    // If no index provided, return all animations from all items
    if (index === undefined) {
      const allAnimations = [];
      game.resolution_queue.forEach((item, idx) => {
        if (item.animations && item.animations.length > 0) {
          allAnimations.push({
            index: idx,
            animations: item.animations
          });
        }
      });
      console.log('All Animations:', allAnimations);
      //return allAnimations;
    }
    
    // Get specific item from resolution queue
    const item = game.resolution_queue[index];
    if (!item) {
      console.warn(`❌ No item at index ${index} in resolution queue`);
      return null;
    }
    
    const animations = item.animations || [];
    console.log(`Animations at index [${index}]:`, animations);
    //return animations;
  },
  
  // === VALIDATION ===
  
  /**
   * Validate all game data
   */
  validateGameData() {
    const game = useGameStore.getState().game;
    if (!game) {
      console.error('❌ No game loaded');
      return;
    }
    
    console.log('🔍 Running validation...');
    validateGameSnapshots(game);
  },
  
  /**
   * Validate specific unit
   */
  validateUnit(unitId) {
    const game = useGameStore.getState().game;
    if (!game) {
      console.error('❌ No game loaded');
      return;
    }
    
    validateUnitAcrossSnapshots(game, unitId);
  },
  
  /**
   * Validate current units
   */
  validateCurrentUnits() {
    const units = useGameStore.getState().getCurrentUnits();
    validateUnits(units, 'Current Units (Manual Check)');
  },
  
  // === ACTIONS & PLANNING ===
  
  /**
   * Get team planned actions
   */
  getPlannedActions() {
    const actions = useGameStore.getState().game?.team_planned_actions || [];
    
    if (actions.length === 0) {
      console.log("No planned actions.");
      return [];
    }
    
    // Calculate origins for better visualization
    const game = useGameStore.getState().game;
    const origins = calculateActionOrigins(game);
    
    // Add calculated origins to each action
    const actionsWithOrigins = actions.map(action => ({
      ...action,
      calculatedOrigin: origins[action.unit_id] || null
    }));
    
    console.table(actionsWithOrigins.map(a => ({
      id: a.id,
      action: a.action?.name || 'Unknown',
      unit: a.unit?.name || `Unit #${a.unit_id}`,
      origin: a.calculatedOrigin ? `(${a.calculatedOrigin.x}, ${a.calculatedOrigin.y})` : 'Unknown',
      target: a.target_data ? `(${a.target_data.x}, ${a.target_data.y})` : 'N/A',
      status: a.status
    })));
    
    return actionsWithOrigins;
  },
  
  // === CINEMATIC ===
  
  /**
   * Get cinematic state
   */
  getCinematicState() {
    const state = useCinematicStore.getState();
    console.groupCollapsed('🎬 Cinematic State');
    console.log('Current Step:', state.currentActionCardIndex);
    console.log('Executed Step:', state.executedResolutionQueueIndex);
    console.log('Is Animating:', state.isAnimating);
    console.log('Auto Play:', state.isAutoPlay);
    console.log('Playback Speed:', state.playbackSpeed);
    console.groupEnd();
    return state;
  },
  
  // === METADATA ===
  
  /**
   * Get game metadata (waits for it to load if necessary)
   * Won't trigger a new fetch if already loading from app initialization
   */
  async getMetadata() {
    const state = useMetadataStore.getState();
    
    // If already loaded, return immediately
    if (state.loaded && state.metadata) {
      console.log('=== Game Metadata (Cached) ===');
      console.log('Loaded:', state.loaded);
      console.log('Loading:', state.loading);
      console.log('Error:', state.error);
      console.log('\n=== Stat Constraints ===');
      console.table(state.metadata.stat_constraints);
      console.log('\n=== Tracked Stats ===');
      console.log(state.metadata.tracked_stats);
      console.log('\n=== Full Metadata ===');
      console.log(state.metadata);
      
      return state.metadata;
    }
    
    // If currently loading, wait for it to complete
    if (state.loading) {
      console.log('Metadata is loading, waiting...');
      
      // Poll until loading is complete (check every 100ms)
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const currentState = useMetadataStore.getState();
          
          if (!currentState.loading) {
            clearInterval(checkInterval);
            
            if (currentState.loaded && currentState.metadata) {
              console.log('Metadata loaded successfully!');
              console.log('\n=== Stat Constraints ===');
              console.table(currentState.metadata.stat_constraints);
              resolve(currentState.metadata);
            } else if (currentState.error) {
              console.error('Metadata loading failed:', currentState.error);
              reject(new Error(currentState.error));
            } else {
              console.error('Metadata loading completed but no data available');
              reject(new Error('No metadata available'));
            }
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Metadata loading timeout'));
        }, 10000);
      });
    }
    
    // If not loaded and not loading, fetch it
    console.log('Metadata not loaded, fetching...');
    try {
      const metadata = await useMetadataStore.getState().fetchMetadata();
      console.log('Metadata fetched successfully!');
      console.log('\n=== Stat Constraints ===');
      console.table(metadata.stat_constraints);
      return metadata;
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      throw error;
    }
  },
  
  /**
   * Force fetch metadata (bypasses cache)
   */
  async fetchMetadata() {
    console.log('Force fetching metadata...');
    try {
      const metadata = await useMetadataStore.getState().fetchMetadata();
      console.log('Metadata fetched successfully!');
      console.log('\n=== Stat Constraints ===');
      console.table(metadata.stat_constraints);
      return metadata;
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      return null;
    }
  },
  
  // === UTILITIES ===
  
  /**
   * Clear console with style
   */
  clear() {
    console.clear();
    console.log('%c🎮 Xhelos Debug Tools Ready', 'color: #549BFF; font-size: 20px; font-weight: bold');
    // console.log('%cType xhelos.help() for commands', 'color: #888; font-style: italic');
  },
  
  /**
   * Show help
   */
  help() {
    console.log('%c🎮 Xhelos Debug Tools', 'color: #549BFF; font-size: 18px; font-weight: bold');
    console.log('%c══════════════════════', 'color: #549BFF');
    
    const commands = [
      ['SNAPSHOTS', ''],
      ['xhelos.getSnapshot(key)', 'Get MERGED snapshot (base/current/1/2...)'],
      ['xhelos.getRawSnapshot(key)', 'Get RAW snapshot/delta (not merged)'],
      ['xhelos.getDeltaSnapshot(n)', 'Get only delta changes at step n'],
      ['xhelos.listSnapshots()', 'List all available snapshots'],
      ['', ''],
      ['UNITS', ''],
      ['xhelos.getUnit(id)', 'Get unit from current state'],
      ['xhelos.getUnitAcrossSnapshots(id)', 'Get unit across all snapshots'],
      ['xhelos.listUnits()', 'List all units in table'],
      ['xhelos.validateUnit(id)', 'Validate specific unit'],
      ['', ''],
      ['GAME STATE', ''],
      ['xhelos.getGameState()', 'Get current computed game state'],
      ['xhelos.getRawGameData()', 'Get raw game data (_rawGame)'],
      ['xhelos.getStores()', 'Get all store states'],
      ['xhelos.validateGameData()', 'Validate all game data'],
      ['', ''],
      ['RESOLUTION', ''],
      ['xhelos.getResolutionQueue()', 'Show full resolution queue'],
      ['xhelos.dumpResolutionQueue()', 'Pretty print resolution queue'],
      ['xhelos.getAnimation(index)', 'Get visual effects at index'],
      ['', ''],
      ['ACTIONS', ''],
      ['xhelos.getPlannedActions()', 'Show planned actions with origins'],
      ['xhelos.getCinematicState()', 'Show cinematic/animation state'],
      ['', ''],
      ['METADATA', ''],
      ['xhelos.getMetadata()', 'Get game metadata (waits if loading)'],
      ['xhelos.fetchMetadata()', 'Force fetch metadata (bypasses cache)'],
      ['', ''],
      ['UTILS', ''],
      ['xhelos.clear()', 'Clear console'],
      ['xhelos.help()', 'Show this help']
    ];
    
    commands.forEach(([cmd, desc]) => {
      if (cmd === '' && desc === '') {
        console.log(''); // Empty line for spacing
      } else if (desc === '') {
        console.log(`%c${cmd}`, 'color: #FFD700; font-weight: bold'); // Section headers
      } else {
        console.log(`  ${cmd}${desc ? ' - ' + desc : ''}`);
      }
    });
    
    console.log('%c══════════════════════', 'color: #549BFF');
    console.log('💡 Tips:');
    console.log('  • getSnapshot() = merged/calculated state');
    console.log('  • getRawSnapshot() = raw delta or base/current');
    console.log('  • getDeltaSnapshot(1) = only what changed at step 1');
  }
};

/**
 * Initialize debug helpers
 */
export function initDebugHelpers() {
  // Only initialize in debug mode
  if (!CONFIG.DEBUG_MODE) return;
  
  // Check if already initialized
  if (window.xhelos && !window.xhelos._partial) return;
  
  // Attach to window
  window.xhelos = xhelosDebugger;

  //console.log('%c🎮 Xhelos Debug Tools Initialized', 'color: #549BFF; font-size: 16px; font-weight: bold');
  // console.log('%cType xhelos.help() for commands', 'color: #888; font-style: italic');
}

// Auto-initialize if imported directly
if (CONFIG.DEBUG_MODE) {
  initDebugHelpers();
}